import axiosInstance from "utils/axios";
import { toast } from "sonner";

// Get all schedule templates
export const getJobScheduleTemplates = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/api/JobSchedule/templates", { params });
    if (response.data.statusCode === 200 && response.data.status === "Success") {
      let data = response.data.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("JSON parsing error for templates:", e);
        }
      }
      return {
        success: true,
        data: Array.isArray(data) ? data : [],
        totalRecords: response.data.totalRecords || 0
      };
    }
    throw new Error(response.data.message || "Failed to fetch templates");
  } catch (error) {
    console.error("Error fetching job schedule templates:", error);
    toast.error(error.message || "Failed to load templates");
    return { success: false, data: [], totalRecords: 0 };
  }
};

// Get tasks for a single schedule template (on-demand — avoids relying on the tasks
// embedded in the templates list response, which can be stale or missing).
export const getJobScheduleTemplateTasks = async (templateId) => {
  try {
    const response = await axiosInstance.get(`/api/JobSchedule/templates/${templateId}/tasks`);
    if (response.data.statusCode === 200 && response.data.status === "Success") {
      let data = response.data.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("JSON parsing error for template tasks:", e);
        }
      }
      return {
        success: true,
        data: Array.isArray(data) ? data : []
      };
    }
    throw new Error(response.data.message || "Failed to fetch template tasks");
  } catch (error) {
    console.error("Error fetching job schedule template tasks:", error);
    toast.error(error.message || "Failed to load template tasks");
    return { success: false, data: [] };
  }
};

// Create or update template name (Uses POST for update now as per backend changes)
export const upsertJobScheduleTemplate = async (templateId, templateName, options = {}) => {
  const { showToast = true } = options;
  try {
    let response;
    if (templateId) {
      response = await axiosInstance.post(`/api/JobSchedule/templates/${templateId}`, {
        templateName
      });
    } else {
      response = await axiosInstance.post("/api/JobSchedule/templates", {
        templateName
      });
    }

    if (response.data.statusCode === 200 && response.data.status === "Success") {
      let data = response.data.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("JSON parsing error for upsert template:", e);
        }
      }
      if (showToast) {
        toast.success(templateId ? "Template updated successfully" : "Template created successfully");
      }
      return { success: true, data };
    }
    throw new Error(response.data.message || "Failed to save template");
  } catch (error) {
    console.error("Error upserting template:", error);
    if (showToast) {
      toast.error(error.message || "Failed to save template");
    }
    return { success: false, error: error.message };
  }
};

// Delete template (Uses POST /api/JobSchedule/DeleteTemplates/{id} now as per backend changes)
export const deleteJobScheduleTemplate = async (templateId, options = {}) => {
  const { showToast = true } = options;
  try {
    const response = await axiosInstance.post(`/api/JobSchedule/DeleteTemplates/${templateId}`);
    if (response.data.statusCode === 200 && response.data.status === "Success") {
      if (showToast) {
        toast.success("Template deleted successfully");
      }
      return { success: true };
    }
    throw new Error(response.data.message || "Failed to delete template");
  } catch (error) {
    console.error("Error deleting template:", error);
    if (showToast) {
      toast.error(error.message || "Failed to delete template");
    }
    return { success: false, error: error.message };
  }
};

// Copy template (Uses POST /api/JobSchedule/templates/{templateId}/copy)
export const copyJobScheduleTemplate = async (templateId, newTemplateName, options = {}) => {
  const { showToast = true } = options;
  try {
    const response = await axiosInstance.post(`/api/JobSchedule/templates/${templateId}/copy`, {
      newTemplateName
    });
    if (response.data.statusCode === 200 && response.data.status === "Success") {
      let data = response.data.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("JSON parsing error for copy template:", e);
        }
      }
      if (showToast) {
        toast.success("Template copied successfully");
      }
      return { success: true, data };
    }
    throw new Error(response.data.message || "Failed to copy template");
  } catch (error) {
    console.error("Error copying template:", error);
    if (showToast) {
      toast.error(error.message || "Failed to copy template");
    }
    return { success: false, error: error.message };
  }
};

// Helper to generate v4 GUID/UUID client-side
const generateUUID = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// Add or update multiple tasks in bulk (Uses POST /api/JobSchedule/templates/{id}/tasks/bulk)
export const upsertJobScheduleTemplateTasksBulk = async (templateId, tasksData) => {
  try {
    const tasksArray = Array.isArray(tasksData) ? tasksData : (tasksData?.tasks || []);

    const mappedTasks = tasksArray.map(t => {
      // Robustly collect designationIds array from possible fields
      const designationIds = t.designationIds || t.DesignationIds ||
        (t.designations || t.Designations || []).map(d => d.designationId || d.DesignationId || d).filter(Boolean) ||
        (t.designationId || t.DesignationId ? [t.designationId || t.DesignationId] : []);

      return {
        templateTaskId: t.templateTaskId || t.TemplateTaskId || generateUUID(),
        taskName: t.taskName || t.TaskName || "",
        designationIds: designationIds,
        categoryId: t.categoryId || t.CategoryId || null,
        parentTaskId: t.parentTaskId || t.ParentTaskId || null,
        taskOrder: (t.taskOrder !== undefined && t.taskOrder !== null) ? t.taskOrder :
          (t.TaskOrder !== undefined && t.TaskOrder !== null) ? t.TaskOrder : null,
        startDate: t.startDate || t.StartDate || null,
        dueDate: t.dueDate || t.DueDate || t.completionDate || t.CompletionDate || null,
        // FK to DropdownOption (Enum=Phase). Always applied, like categoryId -- null clears it.
        // Was previously missing here entirely, so every save silently wiped out the task's
        // configured Phase (the backend SP unconditionally overwrites with whatever is sent).
        phaseOptionId: t.phaseOptionId || t.PhaseOptionId || null,
        dependsOnTemplateTaskIds: t.dependsOnTemplateTaskIds || t.DependsOnTemplateTaskIds || []
      };
    });

    const response = await axiosInstance.post(`/api/JobSchedule/templates/${templateId}/tasks/bulk`, {
      tasks: mappedTasks
    });
    if (response.data.statusCode === 200 && response.data.status === "Success") {
      let data = response.data.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("JSON parsing error for bulk template tasks:", e);
        }
      }
      return { success: true, data };
    }
    throw new Error(response.data.message || "Failed to save template tasks");
  } catch (error) {
    console.error("Error saving template tasks in bulk:", error);
    toast.error(error.message || "Failed to save template tasks");
    return { success: false, error: error.message };
  }
};

// Add a single task to a template
export const createJobScheduleTemplateTask = async (templateId, taskPayload) => {
  try {
    const designationIds = taskPayload.designationIds || (taskPayload.designationId ? [taskPayload.designationId] : []);
    const response = await axiosInstance.post(`/api/JobSchedule/templates/${templateId}/tasks`, {
      taskName: taskPayload.taskName,
      designationIds: designationIds,
      categoryId: taskPayload.categoryId || null,
      parentTaskId: taskPayload.parentTaskId || null,
      taskOrder: taskPayload.taskOrder !== undefined ? taskPayload.taskOrder : null,
      phaseOptionId: taskPayload.phaseOptionId || taskPayload.PhaseOptionId || null
    });
    if (response.data.statusCode === 200 && response.data.status === "Success") {
      let data = response.data.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("JSON parsing error for create template task:", e);
        }
      }
      toast.success("Task added to template");
      return { success: true, data };
    }
    throw new Error(response.data.message || "Failed to add template task");
  } catch (error) {
    console.error("Error creating template task:", error);
    toast.error(error.message || "Failed to add template task");
    return { success: false, error: error.message };
  }
};

// Update a single task on a template
export const updateJobScheduleTemplateTask = async (templateId, taskId, taskPayload) => {
  try {
    const designationIds = taskPayload.designationIds || (taskPayload.designationId ? [taskPayload.designationId] : []);
    const response = await axiosInstance.post(`/api/JobSchedule/templates/${templateId}/tasks/${taskId}`, {
      taskName: taskPayload.taskName,
      designationIds: designationIds,
      categoryId: taskPayload.categoryId || null,
      parentTaskId: taskPayload.parentTaskId || null,
      taskOrder: taskPayload.taskOrder !== undefined ? taskPayload.taskOrder : null,
      phaseOptionId: taskPayload.phaseOptionId || taskPayload.PhaseOptionId || null
    });
    if (response.data.statusCode === 200 && response.data.status === "Success") {
      let data = response.data.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("JSON parsing error for update template task:", e);
        }
      }
      toast.success("Template task updated");
      return { success: true, data };
    }
    throw new Error(response.data.message || "Failed to update template task");
  } catch (error) {
    console.error("Error updating template task:", error);
    toast.error(error.message || "Failed to update template task");
    return { success: false, error: error.message };
  }
};

// Bulk delete template tasks (Uses POST /api/JobSchedule/templates/tasks/bulk-delete)
export const bulkDeleteJobScheduleTemplateTasks = async (taskIds) => {
  try {
    const response = await axiosInstance.post("/api/JobSchedule/templates/tasks/bulk-delete", {
      taskIds
    });
    if (response.data.statusCode === 200 && response.data.status === "Success") {
      toast.success("Template tasks deleted successfully");
      return { success: true };
    }
    throw new Error(response.data.message || "Failed to delete template tasks");
  } catch (error) {
    console.error("Error bulk deleting template tasks:", error);
    toast.error(error.message || "Failed to delete template tasks");
    return { success: false, error: error.message };
  }
};

// Wrapper for single task add/edit utilizing bulk endpoint (retained for backward compatibility)
export const upsertJobScheduleTemplateTask = async (templateId, taskId, taskName, designationIds) => {
  const result = await upsertJobScheduleTemplateTasksBulk(templateId, [
    {
      templateTaskId: taskId || null,
      taskName,
      designationIds,
      isDeleted: false
    }
  ]);

  if (result.success) {
    toast.success(taskId ? "Template task updated" : "Task added to template");
  }

  return result;
};

// Delete template task (Uses POST /api/JobSchedule/DeleteTemplateTask/{id}/tasks/{taskId} now)
export const deleteJobScheduleTemplateTask = async (templateId, taskId) => {
  try {
    const response = await axiosInstance.post(`/api/JobSchedule/DeleteTemplateTask/${templateId}/tasks/${taskId}`);
    if (response.data.statusCode === 200 && response.data.status === "Success") {
      toast.success("Template task deleted");
      return { success: true };
    }
    throw new Error(response.data.message || "Failed to delete template task");
  } catch (error) {
    console.error("Error deleting template task:", error);
    toast.error(error.message || "Failed to delete template task");
    return { success: false, error: error.message };
  }
};

// Bulk delete job checklist tasks (Uses POST /api/JobSchedule/tasks/bulk-delete)
export const bulkDeleteJobScheduleTasks = async (taskIds) => {
  try {
    const response = await axiosInstance.post("/api/JobSchedule/tasks/bulk-delete", {
      taskIds
    });
    if (response.data.statusCode === 200 && response.data.status === "Success") {
      return { success: true };
    }
    throw new Error(response.data.message || "Failed to delete schedules");
  } catch (error) {
    console.error("Error bulk deleting job tasks:", error);
    toast.error(error.message || "Failed to delete schedules");
    return { success: false, error: error.message };
  }
};


// Get panel listing for schedules (jobs, traders, workers)
export const getJobSchedulePanelListing = async (payload) => {
  try {
    const response = await axiosInstance.post("/api/JobSchedule/panel-listing", payload);
    if (response.data.statusCode === 200 && response.data.status === "Success") {
      let data = response.data.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("JSON parsing error for panel listing:", e);
        }
      }
      return {
        success: true,
        data: Array.isArray(data) ? data : [],
        totalRecords: response.data.totalRecords || 0
      };
    }
    throw new Error(response.data.message || "Failed to fetch panel listing");
  } catch (error) {
    console.error("Error fetching panel listing:", error);
    toast.error(error.message || "Failed to load panel listing");
    return { success: false, data: [], totalRecords: 0 };
  }
};

const inFlightCalendarRequests = new Map();

// Get calendar tasks for schedules (deduplicates identical concurrent requests)
export const getJobScheduleCalendar = async (payload) => {
  const requestKey = JSON.stringify(payload);
  if (inFlightCalendarRequests.has(requestKey)) {
    return inFlightCalendarRequests.get(requestKey);
  }

  const promise = (async () => {
    try {
      const response = await axiosInstance.post("/api/JobSchedule/calendar", payload);
      if (response.data.statusCode === 200 && response.data.status === "Success") {
        let data = response.data.data;
        if (typeof data === "string") {
          try {
            data = JSON.parse(data);
          } catch (e) {
            console.error("JSON parsing error for calendar tasks:", e);
          }
        }
        return {
          success: true,
          data: Array.isArray(data) ? data : [],
          totalRecords: response.data.totalRecords || 0
        };
      }
      throw new Error(response.data.message || "Failed to fetch calendar tasks");
    } catch (error) {
      console.error("Error fetching calendar tasks:", error);
      toast.error(error.message || "Failed to load calendar tasks");
      return { success: false, data: [], totalRecords: 0 };
    } finally {
      inFlightCalendarRequests.delete(requestKey);
    }
  })();

  inFlightCalendarRequests.set(requestKey, promise);
  return promise;
};

// Get all job schedule tasks (across all jobs)
export const getAllJobScheduleTasks = async (payload) => {
  try {
    const response = await axiosInstance.post("/api/JobSchedule/tasks", payload);
    if (response.data.statusCode === 200 && response.data.status === "Success") {
      let data = response.data.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("JSON parsing error for all tasks:", e);
        }
      }
      return {
        success: true,
        data: Array.isArray(data) ? data : [],
        totalRecords: response.data.totalRecords || 0
      };
    }
    throw new Error(response.data.message || "Failed to fetch tasks");
  } catch (error) {
    console.error("Error fetching all job schedule tasks:", error);
    toast.error(error.message || "Failed to load tasks");
    return { success: false, data: [], totalRecords: 0 };
  }
};

// Get all checklist tasks for a specific job
export const getJobScheduleTaskFilters = async () => {
  try {
    const response = await axiosInstance.get("/api/JobSchedule/tasks/filter");
    if (response.data.statusCode === 200 && (response.data.status === "Success" || response.data.status === "success")) {
      let data = response.data.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("JSON parsing error for task filters:", e);
        }
      }
      return {
        success: true,
        data: Array.isArray(data) ? data : [],
      };
    }
    throw new Error(response.data.message || "Failed to fetch task filters");
  } catch (error) {
    console.error("Error fetching job schedule task filters:", error);
    toast.error(error.message || "Failed to load task filters");
    return { success: false, data: [] };
  }
};

// Get all checklist tasks for a specific job
export const getJobScheduleTasks = async (jobId, params = {}) => {
  try {
    const response = await axiosInstance.get(`/api/JobSchedule/jobs/${jobId}/tasks`, { params });
    if (response.data.statusCode === 200 && response.data.status === "Success") {
      let data = response.data.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("JSON parsing error for job tasks:", e);
        }
      }
      return {
        success: true,
        data: Array.isArray(data) ? data : [],
        totalRecords: response.data.totalRecords || 0
      };
    }
    throw new Error(response.data.message || "Failed to fetch job tasks");
  } catch (error) {
    console.error("Error fetching job tasks:", error);
    toast.error(error.message || "Failed to load job tasks");
    return { success: false, data: [], totalRecords: 0 };
  }
};

// Add multiple tasks to a job checklist in bulk
export const bulkAddJobScheduleTasks = async (jobId, payload) => {
  try {
    const response = await axiosInstance.post(`/api/JobSchedule/jobs/${jobId}/tasks/bulk`, payload);
    if (response.data.statusCode === 200 && response.data.status === "Success") {
      let data = response.data.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("JSON parsing error for bulk add task data:", e);
        }
      }
      return { success: true, data };
    }
    return {
      success: false,
      error: response.data.message || "Failed to add tasks",
      statusCode: response.data.statusCode,
      data: response.data.data
    };
  } catch (error) {
    console.error("Error bulk adding tasks:", error);
    const errData = error.response?.data || {};
    const message = errData.message || error.message || "Failed to add tasks";
    toast.error(message);
    return {
      success: false,
      error: message,
      statusCode: errData.statusCode || error.response?.status,
      data: errData.data
    };
  }
};

// Get import preview of a template
export const getTemplateImportPreview = async (templateId, jobId, params = {}) => {
  try {
    const isGuid = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(jobId);
    const cleanJobId = isGuid ? jobId : "00000000-0000-0000-0000-000000000000";
    const response = await axiosInstance.get(`/api/JobSchedule/templates/${templateId}/import-preview/${cleanJobId}`, { params });
    if (response.data.statusCode === 200 && response.data.status === "Success") {
      let data = response.data.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("JSON parsing error for template preview:", e);
        }
      }
      return { success: true, data: data || {} };
    }
    throw new Error(response.data.message || "Failed to load template preview");
  } catch (error) {
    console.error("Error fetching template preview:", error);
    toast.error(error.message || "Failed to load template preview");
    return { success: false, data: {} };
  }
};

// Import template tasks to job
export const importTemplateToJob = async (jobId, payload) => {
  try {
    const response = await axiosInstance.post(`/api/JobSchedule/jobs/${jobId}/import`, payload);
    if (response.data.statusCode === 200 && response.data.status === "Success") {
      return { success: true, data: response.data };
    }
    return {
      success: false,
      error: response.data.message || "Failed to import template",
      statusCode: response.data.statusCode,
      data: response.data.data
    };
  } catch (error) {
    console.error("Error importing template:", error);
    const errData = error.response?.data || {};
    const message = errData.message || error.message || "Failed to import template";
    toast.error(message);
    return {
      success: false,
      error: message,
      statusCode: errData.statusCode || error.response?.status,
      data: errData.data
    };
  }
};

// Go online with offline tasks
export const goOnlineJobSchedule = async (jobId) => {
  try {
    const response = await axiosInstance.post(`/api/JobSchedule/jobs/${jobId}/go-online`);
    if (response.data.statusCode === 200 && response.data.status === "Success") {
      toast.success("Job schedule is now online");
      return { success: true };
    }
    throw new Error(response.data.message || "Failed to set online");
  } catch (error) {
    console.error("Error setting online:", error);
    toast.error(error.message || "Failed to set online");
    return { success: false, error: error.message };
  }
};

// Go online with specific selected offline tasks
export const goOnlineJobScheduleTasks = async (taskIds) => {
  try {
    const response = await axiosInstance.post("/api/JobSchedule/tasks/go-online", {
      taskIds
    });
    if (response.data.statusCode === 200 && response.data.status === "Success") {
      // toast.success("Selected schedules are now online");
      return { success: true };
    }
    throw new Error(response.data.message || "Failed to set online");
  } catch (error) {
    console.error("Error setting online:", error);
    toast.error(error.message || "Failed to set online");
    return { success: false, error: error.message };
  }
};

// Go offline with specific selected published tasks
export const goOfflineJobScheduleTasks = async (taskIds) => {
  try {
    const response = await axiosInstance.post("/api/JobSchedule/tasks/go-offline", {
      taskIds
    });
    if (response.data.statusCode === 200 && response.data.status === "Success") {
      // toast.success("Selected schedules are now offline");
      return { success: true };
    }
    throw new Error(response.data.message || "Failed to set offline");
  } catch (error) {
    console.error("Error setting offline:", error);
    toast.error(error.message || "Failed to set offline");
    return { success: false, error: error.message };
  }
};

// Update a job task (e.g. name, notes, due date, assignees)
export const updateJobScheduleTask = async (jobId, taskId, payload) => {
  try {
    const response = await axiosInstance.post(`/api/JobSchedule/jobs/${jobId}/tasks/${taskId}`, payload);
    if (response.data.statusCode === 200 && response.data.status === "Success") {
      toast.success("Task updated successfully");
      let data = response.data.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("JSON parsing error for update task data:", e);
        }
      }
      return { success: true, data };
    }
    return {
      success: false,
      error: response.data.message || "Failed to update task",
      statusCode: response.data.statusCode,
      data: response.data.data
    };
  } catch (error) {
    console.error("Error updating task:", error);
    const errData = error.response?.data || {};
    const message = errData.message || error.message || "Failed to update task";
    toast.error(message);
    return {
      success: false,
      error: message,
      statusCode: errData.statusCode || error.response?.status,
      data: errData.data
    };
  }
};

// Adjust the lag on one or more predecessor dependency edges. LagBasis (which predecessor date
// the lag is measured against) is resolved server-side per edge, so the caller just supplies the
// new lag. adjustments: [{ JobTaskId, DependsOnTaskId, NewLagDays }]
export const adjustJobScheduleTaskLag = async (adjustments) => {
  try {
    const response = await axiosInstance.post(`/api/JobSchedule/tasks/adjust-lag`, { Adjustments: adjustments });
    if (response.data.statusCode === 200 && response.data.status === "Success") {
      let data = response.data.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("JSON parsing error for adjust lag data:", e);
        }
      }
      return { success: true, data };
    }
    return {
      success: false,
      error: response.data.message || "Failed to adjust lag",
      statusCode: response.data.statusCode
    };
  } catch (error) {
    console.error("Error adjusting lag:", error);
    const errData = error.response?.data || {};
    const message = errData.message || error.message || "Failed to adjust lag";
    toast.error(message);
    return {
      success: false,
      error: message,
      statusCode: errData.statusCode || error.response?.status
    };
  }
};

// Delete a job task
export const deleteJobScheduleTask = async (jobId, taskId) => {
  try {
    const response = await axiosInstance.post(`/api/JobSchedule/DeleteJobTask/${jobId}/tasks/${taskId}`);
    if (response.data.statusCode === 200 && response.data.status === "Success") {
      // toast.success("Task deleted successfully");
      return { success: true };
    }
    throw new Error(response.data.message || "Failed to delete task");
  } catch (error) {
    console.error("Error deleting task:", error);
    toast.error(error.message || "Failed to delete task");
    return { success: false, error: error.message };
  }
};

// Mark assignee task as done or undone
export const markJobScheduleTaskAssigneeDone = async (assigneeId, isDone) => {
  try {
    const response = await axiosInstance.post(`/api/JobSchedule/assignees/${assigneeId}/done`, {
      isDone
    });
    if (response.data.statusCode === 200 && response.data.status === "Success") {
      return { success: true };
    }
    throw new Error(response.data.message || "Failed to update task status");
  } catch (error) {
    console.error("Error updating task status:", error);
    toast.error(error.message || "Failed to update task status");
    return { success: false, error: error.message };
  }
};

// Bulk mark job checklist tasks as done or undone (Uses POST /api/JobSchedule/tasks/bulk-done)
export const bulkMarkJobScheduleTasksDone = async (taskIds, isDone = true) => {
  try {
    const response = await axiosInstance.post("/api/JobSchedule/tasks/bulk-done", {
      taskIds,
      isDone
    });
    if (response.data.statusCode === 200 && response.data.status === "Success") {
      return { success: true };
    }
    throw new Error(response.data.message || "Failed to update task status");
  } catch (error) {
    console.error("Error bulk updating task status:", error);
    toast.error(error.message || "Failed to update task status");
    return { success: false, error: error.message };
  }
};

// Bulk mark job checklist tasks as N/A or not N/A (Uses POST /api/JobSchedule/tasks/bulk-na)
export const bulkMarkJobScheduleTasksNA = async (taskIds, isNA = true) => {
  try {
    const response = await axiosInstance.post("/api/JobSchedule/tasks/bulk-na", {
      taskIds,
      isNA
    });
    if (response.data.statusCode === 200 && response.data.status === "Success") {
      return { success: true };
    }
    throw new Error(response.data.message || "Failed to update task N/A status");
  } catch (error) {
    console.error("Error bulk updating task N/A status:", error);
    toast.error(error.message || "Failed to update task N/A status");
    return { success: false, error: error.message };
  }
};


// Get specific job checklist task details
export const getJobScheduleTaskDetail = async (jobId, taskId) => {
  try {
    const response = await axiosInstance.get(`/api/JobSchedule/jobs/${jobId}/tasks/${taskId}`);
    if (response.data.statusCode === 200 && response.data.status === "Success") {
      let data = response.data.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("JSON parsing error for task details:", e);
        }
      }
      return { success: true, data };
    }
    throw new Error(response.data.message || "Failed to load task details");
  } catch (error) {
    console.error("Error loading task details:", error);
    toast.error(error.message || "Failed to load task details");
    return { success: false, error: error.message };
  }
};

// Update assignee task status
export const updateJobScheduleTaskAssigneeStatus = async (assigneeId, taskStatusId) => {
  try {
    const response = await axiosInstance.post(`/api/JobSchedule/assignees/${assigneeId}/status`, {
      taskStatusId
    });
    if (response.data.statusCode === 200 && response.data.status === "Success") {
      return { success: true };
    }
    throw new Error(response.data.message || "Failed to update assignee status");
  } catch (error) {
    console.error("Error updating assignee status:", error);
    toast.error(error.message || "Failed to update assignee status");
    return { success: false, error: error.message };
  }
};

// Get all schedule/task statuses
export const getJobScheduleTaskStatuses = async () => {
  try {
    const response = await axiosInstance.get("/api/JobSchedule/task-statuses");
    if (response.data.statusCode === 200 && response.data.status === "Success") {
      let data = response.data.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("JSON parsing error for task statuses:", e);
        }
      }
      return { success: true, data: Array.isArray(data) ? data : [] };
    }
    throw new Error(response.data.message || "Failed to fetch task statuses");
  } catch (error) {
    console.error("Error fetching task statuses:", error);
    return { success: false, data: [] };
  }
};

// Print schedule unpaginated events
export const printJobSchedule = async (payload) => {
  try {
    const response = await axiosInstance.post("/api/JobSchedule/print", payload);
    if (response.data.statusCode === 200 && response.data.status === "Success") {
      let data = response.data.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("JSON parsing error for print tasks:", e);
        }
      }
      return {
        success: true,
        data: Array.isArray(data) ? data : [],
        totalRecords: response.data.totalRecords || 0
      };
    }
    throw new Error(response.data.message || "Failed to fetch print tasks");
  } catch (error) {
    console.error("Error fetching print tasks:", error);
    toast.error(error.message || "Failed to load print tasks");
    return { success: false, data: [], totalRecords: 0 };
  }
};


// Get workers groups
export const getWorkersGroups = async (payload) => {
  try {
    const response = await axiosInstance.post("/api/WorkersGroup/get-groups", payload);
    if (response.data.statusCode === 200 && (response.data.status === "Success" || response.data.status === "success")) {
      let data = response.data.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("JSON parsing error for groups:", e);
        }
      }
      return {
        success: true,
        data: Array.isArray(data) ? data : [],
        totalRecords: response.data.totalRecords || 0
      };
    }
    throw new Error(response.data.message || "Failed to fetch groups");
  } catch (error) {
    console.error("Error fetching groups:", error);
    toast.error(error.message || "Failed to load groups");
    return { success: false, data: [], totalRecords: 0 };
  }
};

// Upsert workers group
export const upsertWorkersGroup = async (payload) => {
  try {
    const response = await axiosInstance.post("/api/WorkersGroup/upsert", payload);
    if (response.data.statusCode === 200 && (response.data.status === "Success" || response.data.status === "success")) {
      toast.success(payload.workerGroupId ? "Group updated successfully" : "Group created successfully");
      return { success: true, data: response.data.data };
    }
    throw new Error(response.data.message || "Failed to save group");
  } catch (error) {
    console.error("Error saving workers group:", error);
    toast.error(error.message || "Failed to save group");
    return { success: false, error: error.message };
  }
};

// Delete workers group
export const deleteWorkersGroup = async (groupId) => {
  try {
    const response = await axiosInstance.post(`/api/WorkersGroup/Delete/${groupId}`);
    if (response.data.statusCode === 200 && (response.data.status === "Success" || response.data.status === "success")) {
      toast.success("Group deleted successfully");
      return { success: true };
    }
    throw new Error(response.data.message || "Failed to delete group");
  } catch (error) {
    console.error("Error deleting workers group:", error);
    toast.error(error.message || "Failed to delete group");
    return { success: false, error: error.message };
  }
};

// Get grid / Excel view data for schedules (jobs, traders, workers)
export const getJobScheduleGridView = async (payload) => {
  try {
    const response = await axiosInstance.post("/api/JobSchedule/grid-view", payload);
    if (response.data.statusCode === 200 && response.data.status === "Success") {
      let data = response.data.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("JSON parsing error for grid view:", e);
        }
      }
      return {
        success: true,
        data: Array.isArray(data) ? data : [],
        totalRecords: response.data.totalRecords || 0,
      };
    }
    throw new Error(response.data.message || "Failed to fetch grid view");
  } catch (error) {
    console.error("Error fetching grid view:", error);
    toast.error(error.message || "Failed to load grid view");
    return { success: false, data: [], totalRecords: 0 };
  }
};

// Get unpaginated grid / Excel view data for exporting (printing) schedules (jobs, traders, workers)
export const printJobScheduleGridView = async (payload) => {
  try {
    const response = await axiosInstance.post("/api/JobSchedule/grid-view/print", payload);
    if (response.data.statusCode === 200 && response.data.status === "Success") {
      let data = response.data.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("JSON parsing error for grid view print:", e);
        }
      }
      return {
        success: true,
        data: Array.isArray(data) ? data : [],
        totalRecords: response.data.totalRecords || 0,
      };
    }
    throw new Error(response.data.message || "Failed to fetch grid view export data");
  } catch (error) {
    console.error("Error fetching grid view export data:", error);
    toast.error(error.message || "Failed to load export data");
    return { success: false, data: [], totalRecords: 0 };
  }
};

// Get users for schedule list filter
export const getUsersForScheduleListFilter = async () => {
  try {
    const response = await axiosInstance.get("/api/JobSchedule/GetUsersForScheduleListFilter");
    if (response.data.statusCode === 200 && (response.data.status === "Success" || response.data.status === "success")) {
      let data = response.data.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("JSON parsing error for users filter:", e);
        }
      }
      return {
        success: true,
        data: Array.isArray(data) ? data : [],
      };
    }
    throw new Error(response.data.message || "Failed to fetch users");
  } catch (error) {
    console.error("Error fetching users for schedule filter:", error);
    toast.error(error.message || "Failed to load users");
    return { success: false, data: [] };
  }
};
